import { Autocomplete, Checkbox, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import React, { useState } from "react";
import { ErrorModal } from "@dexteel/mesf-core";
import { Asset } from "./Asset";
import { getAssets } from "./AssetRepository";

type AssetOption = {
	label: string;
	value: number | null;
	isAreaAsset: boolean;
};

export const useAssets = (onError?: (error: Error) => void) => {
	return useQuery<Asset[], Error>({
		queryKey: ["assets"],
		queryFn: ({ signal }) => getAssets(signal),
		onError: onError,
	});
};

type SharedProps = {
	label?: string;
	filterAreaAssets?: boolean;
	disabled?: boolean;
	isFilter?: boolean;
};

type SingleSelectProps = SharedProps & {
	multiple?: false;
	value: number | null;
	onChange: (value: number | null) => void;
};

type MultipleSelectProps = SharedProps & {
	multiple: true;
	value: number[];
	onChange: (value: number[]) => void;
};

type Props = SingleSelectProps | MultipleSelectProps;

export const AssetsFilter = ({
	label = "Asset",
	value,
	onChange,
	filterAreaAssets = false,
	disabled = false,
	isFilter = false,
	multiple = false,
}: Props) => {
	const [error, setError] = useState("");
	const isMultiple = multiple === true;
	const multipleValue = isMultiple ? (value as number[]) : [];
	const singleValue = !isMultiple ? (value as number | null) : null;
	const handleSingleChange = onChange as (value: number | null) => void;
	const handleMultipleChange = onChange as (value: number[]) => void;

	const { data: assetsList, isLoading } = useAssets((error) =>
		setError(error.message),
	);

	const options: AssetOption[] = [
		...(isFilter ? [{ label: "All", value: null, isAreaAsset: true }] : []),
		...(Array.isArray(assetsList)
			? assetsList.map((asset: Asset) => ({
					label: asset.AssetName,
					value: asset.AssetId,
					isAreaAsset: asset.CanBeDefaultAsset,
				}))
			: []),
	];

	const filteredOptions = filterAreaAssets
		? options.filter((option) => option.isAreaAsset)
		: options;

	const selectedOptions = isMultiple
		? filteredOptions.filter(
				(option) =>
					option.value != null && multipleValue.includes(option.value),
			)
		: [];

	const selectedOption = !isMultiple
		? filteredOptions.find(
				(option) => option.value === (singleValue || null),
			) ||
			filteredOptions[0] ||
			null
		: null;

	return (
		<>
			<Autocomplete
				multiple={isMultiple}
				size="small"
				options={filteredOptions}
				value={isMultiple ? selectedOptions : selectedOption}
				onChange={(event, newValue) => {
					if (isMultiple) {
						handleMultipleChange(
							(newValue as AssetOption[])
								.map((option) => option.value)
								.filter((option): option is number => option != null),
						);
						return;
					}

					if (newValue) {
						handleSingleChange((newValue as AssetOption).value ?? null);
					}
				}}
				getOptionLabel={(option) => option.label}
				isOptionEqualToValue={(option, value) => option.value === value.value}
				disableCloseOnSelect={isMultiple}
				renderOption={(props, option, state) => (
					<li {...props} key={option.value}>
						{isMultiple ? (
							<Checkbox checked={state.selected} size="small" sx={{ mr: 1 }} />
						) : null}
						{option.label}
					</li>
				)}
				disabled={isLoading || disabled}
				disablePortal
				disableClearable={!isMultiple}
				renderInput={(params) => (
					<TextField
						{...params}
						label={label}
						variant="outlined"
						size="small"
						fullWidth
						placeholder={isMultiple ? "Select assets" : undefined}
					/>
				)}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
